import { IconDragDrop } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import type { UploadFile } from "antd";
import {
	Alert,
	Divider,
	Flex,
	Form,
	Input,
	Select,
	Space,
	Tag,
	Typography,
	Upload,
} from "antd";
import { useEffect, useState } from "react";
import {
	MAX_TERRAFORM_FILE_BYTES,
	useAnalyzeTerraform,
	useAwsListStacks,
	useCreateDeployment,
	useRefreshDeployment,
} from "@/common/artemis-operations/deployment";
import { useEnvironments } from "@/common/artemis-operations/environments";
import { AWS_REGIONS, AwsRegionSelect } from "@/common/aws";
import { ERROR_NETWORK_DESC, ERROR_NETWORK_TITLE } from "@/common/messages";
import {
	type Environment,
	EnvironmentClassification,
	EnvironmentProvider,
} from "@/common/model";
import { useNotify } from "@/common/use-notify";
import {
	EnvironmentClassificationBadge,
	EnvironmentProviderBadge,
} from "@/components/environments/environment-badges";
import { CustomDrawer } from "@/components/layout/workspace/custom-drawer";
import { SegmentedSelection } from "@/components/segmented-selection";
import { Wizard } from "@/components/wizard";
import { FullSpin } from "../base";

const DEPLOYMENT_CREATED_TITLE = "Deployment created";
const DEPLOYMENT_CREATED_DESC = "The deployment was created successfully.";
const ERROR_DEPLOYMENT_CREATION_TITLE = "Error creating deployment";
const ERROR_DEPLOYMENT_CREATION_DESC =
	"The deployment could not be created. Please try again.";
const ERROR_TERRAFORM_ANALYSIS_TITLE = "Error analyzing Terraform files";
const ERROR_TERRAFORM_ANALYSIS_DESC =
	"The Terraform files could not be analyzed. Please try again.";
const ERROR_TERRAFORM_FILE_TOO_LARGE_TITLE = "File is too large";

type DiscoverMode = "yes" | "no";
type IacType = "cloudformation" | "terraform";

const IAC_OPTIONS: Array<{
	value: IacType;
	label: string;
	description: string;
	disabled?: boolean;
}> = [
	{
		value: "cloudformation",
		label: "CloudFormation",
		description: "AWS-native IaC - supports stack auto-discovery",
	},
	{
		value: "terraform",
		label: "Terraform",
		description: "Upload your .tfstate and .tf files to map deployed resources",
	},
];

const DISCOVER_OPTIONS: Array<{
	value: DiscoverMode;
	label: string;
	description: string;
}> = [
	{
		value: "yes",
		label: "Auto-discover",
		description: "Select CloudFormation stacks to build the deployment graph",
	},
	{
		value: "no",
		label: "Not now",
		description: "Create the deployment without discovering infrastructure",
	},
];

interface DeploymentCreateWizardProps {
	open: boolean;
	onClose: () => void;
	/** Called after the deployment is created (and discovery kicked off, when applicable). */
	onCreated?: () => void;
	projectId: string;
	workspaceId: string;
}

/** Wizard for creating a new deployment, linking a project to an environment. */
export function DeploymentCreateWizard({
	open,
	onClose,
	onCreated,
	projectId,
	workspaceId,
}: DeploymentCreateWizardProps) {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [currentStep, setCurrentStep] = useState(0);
	const selectedEnvironmentId: string | null =
		Form.useWatch("environmentId", form) ?? null;
	const [selectedStacks, setSelectedStacks] = useState<string[]>([]);
	const [region, setRegion] = useState<string>(AWS_REGIONS[0].value);
	const iacType: IacType = Form.useWatch("iacType", form) ?? "cloudformation";
	const discoverMode: DiscoverMode =
		Form.useWatch("discoverMode", form) ?? "yes";
	const terraformFiles: UploadFile[] =
		Form.useWatch("terraformFiles", form) ?? [];

	const notify = useNotify();
	const queryClient = useQueryClient();
	const createDeployment = useCreateDeployment();
	const { analyzeTerraform } = useAnalyzeTerraform(projectId, workspaceId);

	const { data: environments = [] } = useEnvironments(workspaceId);
	const selectedEnvironment = environments.find(
		(e) => e?.id === selectedEnvironmentId,
	) as Environment | undefined;
	const isAws = selectedEnvironment?.provider === EnvironmentProvider.AWS;
	const shouldDiscover =
		isAws && iacType === "cloudformation" && discoverMode === "yes";
	const isTerraform = iacType === "terraform";
	const { refresh: refreshDeployment } = useRefreshDeployment(
		selectedEnvironmentId ?? "",
	);

	const {
		data: stacks = [],
		isFetching: stacksFetching,
		error,
	} = useAwsListStacks(
		shouldDiscover && currentStep > 0 ? (selectedEnvironmentId ?? "") : "",
		region,
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: selectedEnvironmentId intentionally triggers stale-stack reset
	useEffect(() => {
		setSelectedStacks([]);
		// Default the region to the env's DefaultRegion when it has one.
		const defaultRegion = selectedEnvironment?.metadata?.find(
			(m) => m?.key === "DefaultRegion",
		)?.value;
		setRegion(defaultRegion || AWS_REGIONS[0].value);
	}, [selectedEnvironmentId]);

	const reset = () => {
		setLoading(false);
		setCurrentStep(0);
		form.resetFields();
		setSelectedStacks([]);
		setRegion(AWS_REGIONS[0].value);
	};

	const handleComplete = () => {
		form.validateFields().then((vals) => {
			setLoading(true);
			const withAwsConfig = shouldDiscover && selectedStacks.length > 0;
			const withTerraform = isTerraform && terraformFiles.length > 0;
			createDeployment(
				{
					projectId,
					environmentId: vals.environmentId as string,
					name: (vals.name as string).trim(),
					description: vals.description
						? (vals.description as string).trim()
						: undefined,
					awsConfig: withAwsConfig
						? { stackNames: selectedStacks, region }
						: undefined,
					// Recorded at creation, not on first successful analysis: otherwise a
					// deployment whose analysis failed looks like a CloudFormation one and gets
					// offered a Discover action that cannot work for it.
					isTerraform,
				},
				{
					onSuccess: (data) => {
						notify.success(DEPLOYMENT_CREATED_TITLE, DEPLOYMENT_CREATED_DESC);
						if (withAwsConfig) {
							// Kick off discovery, THEN refresh the project — so the new card is fetched
							// with isDiscovering already true and shows the "Discovering" pulse.
							refreshDeployment(data.id, {
								onSuccess: () => {
									queryClient.invalidateQueries({
										queryKey: ["project", projectId],
									});
									onCreated?.();
								},
							});
						} else if (withTerraform) {
							// Same idea: start the analysis before refetching, so the card appears
							// already discovering. The files are read once and then deleted.
							analyzeTerraform({
								deploymentId: data.id,
								files: terraformFiles
									.map((f) => f.originFileObj as File | undefined)
									.filter((f): f is File => !!f),
							})
								.catch((e: unknown) =>
									notify.error(
										ERROR_TERRAFORM_ANALYSIS_TITLE,
										e instanceof Error
											? e.message
											: ERROR_TERRAFORM_ANALYSIS_DESC,
									),
								)
								.finally(() => onCreated?.());
						} else {
							queryClient.invalidateQueries({
								queryKey: ["project", projectId],
							});
							onCreated?.();
						}
						onClose();
						reset();
					},
					onError: () => {
						setLoading(false);
						notify.error(
							ERROR_DEPLOYMENT_CREATION_TITLE,
							ERROR_DEPLOYMENT_CREATION_DESC,
						);
					},
				},
			);
		});
	};

	const environmentItems = environments.map((env) => ({
		value: env?.id,
		label: env?.name,
		provider: env?.provider,
		classification: env?.classification,
	}));

	const stackItems = stacks.map((s: string) => ({
		value: s,
		label: s,
	}));

	const pages = [
		{
			id: "environment",
			title: "Environment",
			fields: ["name", "environmentId"],
			content: (
				<Flex vertical gap={16} style={{ width: "100%" }}>
					<Form.Item name="name" label="Name" rules={[{ required: true }]}>
						<Input placeholder="e.g. Production deployment" />
					</Form.Item>
					<Form.Item name="description" label="Description">
						<Input.TextArea
							rows={3}
							placeholder="Optional description of this deployment"
						/>
					</Form.Item>
					<Divider style={{ margin: 0 }}>Environment</Divider>
					<Form.Item name="environmentId" noStyle rules={[{ required: true }]}>
						<Select
							style={{ width: "100%" }}
							placeholder="Select an environment"
							options={environmentItems}
							optionRender={(option) => (
								<Flex align="center" justify="space-between" gap={8}>
									<Flex align="center" gap={8}>
										<Typography.Text>{option.data.label}</Typography.Text>
									</Flex>
									<Space>
										<EnvironmentProviderBadge
											type={option.data.provider || EnvironmentProvider.OTHER}
										/>
										<EnvironmentClassificationBadge
											classification={
												option.data.classification ||
												EnvironmentClassification.NON_PRODUCTION
											}
										/>
									</Space>
								</Flex>
							)}
						/>
					</Form.Item>
				</Flex>
			),
		},
		{
			id: "application",
			title: "Application",
			content: (
				<Flex vertical gap={16} style={{ width: "100%" }}>
					<Typography.Text type="secondary">
						Select the infrastructure-as-code tool used for this deployment.
					</Typography.Text>
					{/* CloudFormation is AWS-only: offering it for another provider leads to a
					    stack list that can never be fetched. */}
					<Form.Item
						name="iacType"
						noStyle
						initialValue={"cloudformation" satisfies IacType}
					>
						<SegmentedSelection
							columns={isAws ? 2 : 1}
							options={
								isAws
									? IAC_OPTIONS
									: IAC_OPTIONS.filter((o) => o.value !== "cloudformation")
							}
						/>
					</Form.Item>

					{!isAws && !isTerraform && (
						<Alert
							type="info"
							title="No infrastructure-as-code selected"
							description="This environment is not connected to AWS, so CloudFormation stacks cannot be discovered. Choose Terraform and supply the state files, or create the deployment now without a resource graph."
						/>
					)}

					{iacType === "cloudformation" && isAws && (
						<>
							<Divider style={{ margin: 0 }}>Discovery</Divider>
							<Typography.Text type="secondary">
								Auto-discover deployed resources from CloudFormation stacks?
							</Typography.Text>
							<Form.Item
								name="discoverMode"
								noStyle
								initialValue={"yes" satisfies DiscoverMode}
							>
								<SegmentedSelection options={DISCOVER_OPTIONS} />
							</Form.Item>
							{discoverMode === "yes" && (
								<>
									<Form.Item label="Region" style={{ marginBottom: 0 }}>
										<AwsRegionSelect value={region} onChange={setRegion} />
									</Form.Item>
									{error && (
										<Alert
											type="error"
											title={ERROR_NETWORK_TITLE}
											description={ERROR_NETWORK_DESC}
										/>
									)}
									{stacksFetching && <FullSpin />}
									{!error && !stacksFetching && stacks.length === 0 && (
										<Typography.Text type="secondary">
											No active stacks found in this environment.
										</Typography.Text>
									)}
									{!error && stacks.length > 0 && (
										<Select
											mode="multiple"
											style={{ width: "100%" }}
											placeholder="Select stacks…"
											value={selectedStacks}
											onChange={setSelectedStacks}
											options={stackItems}
											tagRender={({ label, closable, onClose }) => (
												<Tag closable={closable} onClose={onClose}>
													{label}
												</Tag>
											)}
										/>
									)}
								</>
							)}
						</>
					)}

					{isTerraform && (
						<>
							<Divider style={{ margin: 0 }}>Terraform files</Divider>
							<Typography.Text type="secondary">
								Upload your <code>.tfstate</code> <strong>and</strong> your{" "}
								<code>.tf</code> sources together — the state says what exists,
								the source says how it fits together, so supplying both gives a
								noticeably more accurate graph than the state alone.
							</Typography.Text>
							<Form.Item
								name="terraformFiles"
								valuePropName="fileList"
								getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
								rules={[
									{
										required: true,
										message: "Select at least one Terraform file",
									},
								]}
							>
								<Upload.Dragger
									multiple
									accept=".tfstate,.tf,.json,.backup"
									// false keeps antd from uploading it itself (we upload to a
									// presigned POST later); LIST_IGNORE drops an oversized file
									// on the spot, since the signed policy would reject it anyway.
									beforeUpload={(file) => {
										if (file.size > MAX_TERRAFORM_FILE_BYTES) {
											notify.error(
												ERROR_TERRAFORM_FILE_TOO_LARGE_TITLE,
												`"${file.name}" is larger than the ${MAX_TERRAFORM_FILE_BYTES / 1024 / 1024} MB limit and was not added.`,
											);
											return Upload.LIST_IGNORE;
										}
										return false;
									}}
								>
									<p className="ant-upload-drag-icon">
										<IconDragDrop size={32} />
									</p>
									<p className="ant-upload-text">
										Click or drag Terraform files here
									</p>
									<p className="ant-upload-hint">
										e.g. terraform.tfstate together with main.tf. The files are
										read once to build the graph, then deleted.
									</p>
								</Upload.Dragger>
							</Form.Item>
						</>
					)}
				</Flex>
			),
		},
	];

	// A deployment links a project to an environment, so at least one is required.
	if (!environments.some((e) => e != null)) {
		return (
			<CustomDrawer
				title="New Deployment"
				open={open}
				onClose={onClose}
				size="large"
			>
				<Alert
					type="info"
					title="Add an environment first"
					description="To create a deployment you need at least one environment in this workspace. Add an environment, then create the deployment."
				/>
			</CustomDrawer>
		);
	}

	return (
		<Wizard
			open={open}
			title="New Deployment"
			completeLabel="Create"
			form={form}
			loading={loading}
			onComplete={handleComplete}
			onStepChange={setCurrentStep}
			onCancel={() => {
				onClose();
				reset();
			}}
			pages={pages}
		/>
	);
}
